import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Dropzone from 'react-dropzone';
import { Button, Stack, Typography } from '@mui/material';
import StyledImageUploadButton from 'common/theme/fields/image-upload/StyledImageUploadButton';
import StyledImageUploadContainer from 'common/theme/fields/image-upload/StyledImageUploadContainer';
import StyledImageUploadInnerContainer from 'common/theme/fields/image-upload/StyledImageUploadInnerContainer';
import StyledImageUploadPreview from 'common/theme/fields/image-upload/StyledImageUploadPreview';
import StyledImageUploadPreviewContainer from 'common/theme/fields/image-upload/StyledImageUploadPreviewContainer';
import StyledImageUploadPreviewInnerContainer from 'common/theme/fields/image-upload/StyledImageUploadPreviewInnerContainer';
import StyledImageUploadResetButton from 'common/theme/fields/image-upload/StyledImageUploadResetButton';
import StyledImageUploadResetIcon from 'common/theme/fields/image-upload/StyledImageUploadResetIcon';
import StyledImageUploadText from 'common/theme/fields/image-upload/StyledImageUploadText';
import AvatarCropModal from 'components/shared/modals/AvatarCropModal';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

function ImageUploadAdapter({
  allowedFileTypes,
  input,
  label,
  meta,
  crop = false,
  cropAspect = 16 / 9,
  cropShape = 'rect',
  ...rest
}) {
  const { t } = useTranslation(['buttons', 'errors', 'labels']);
  const openRef = useRef(null);
  const blobUrlRef = useRef(null);
  const cropSrcRef = useRef(null);
  const dispatch = useDispatch();
  const [preview, setPreview] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);

  const commitFile = (file) => {
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    const url = URL.createObjectURL(file);
    blobUrlRef.current = url;
    setPreview(url);
    input.onChange(file);
  };

  const closeCrop = () => {
    if (cropSrcRef.current) {
      URL.revokeObjectURL(cropSrcRef.current);
      cropSrcRef.current = null;
    }
    setCropSrc(null);
    setCropOpen(false);
  };

  // Load existing image URL when form initializes with a string value
  useEffect(() => {
    if (typeof input.value === 'string' && input.value) {
      setPreview(input.value);
    }
  }, [input.value]);

  // Revoke blob URL on unmount to avoid memory leaks
  useEffect(() => () => {
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    if (cropSrcRef.current) URL.revokeObjectURL(cropSrcRef.current);
  }, []);

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    // Con crop: abre el modal de ajuste antes de comprometer el archivo.
    if (crop) {
      if (cropSrcRef.current) URL.revokeObjectURL(cropSrcRef.current);
      const src = URL.createObjectURL(acceptedFiles[0]);
      cropSrcRef.current = src;
      setCropSrc(src);
      setCropOpen(true);
      return;
    }
    commitFile(acceptedFiles[0]);
  };

  const handleCropSave = (file) => {
    commitFile(file);
    closeCrop();
  };

  const onDropRejected = () => {
    const snack = createSnack({
      key: `upload-file-notification-error`,
      variant: 'error',
      message: t('upload.size.file', { ns: 'errors' })
    });

    dispatch(uiSlice.actions.enqueueSnackbar(snack));
  };

  const onReset = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPreview(null);
    input.onChange(null);
  };

  const openDialog = () => {
    if (openRef.current) openRef.current();
  };

  return (
    <>
    <Dropzone
      accept={allowedFileTypes}
      noClick
      noKeyboard
      maxFiles={1}
      maxSize={10485760}
      multiple={false}
      onDrop={onDrop}
      onDropRejected={onDropRejected}
      {...rest}>
      {({ getRootProps, getInputProps, open }) => {
        openRef.current = open;
        return (
        <StyledImageUploadContainer {...getRootProps()}>
          <input style={{ display: 'none' }} {...getInputProps()} />
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {label}
          </Typography>
          <StyledImageUploadInnerContainer>
            {preview ? (
              <StyledImageUploadPreviewContainer>
                <StyledImageUploadPreviewInnerContainer>
                  <StyledImageUploadPreview
                    src={preview}
                    alt={t('preview', { ns: 'labels' })}
                  />
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Button
                      color="primary"
                      onClick={openDialog}
                      size="small"
                      type="button"
                      variant="outlined">
                      Cambiar
                    </Button>
                    <StyledImageUploadResetButton
                      color="secondary"
                      onClick={onReset}
                      size="small"
                      variant="contained">
                      {t('delete', { ns: 'common' })}
                    </StyledImageUploadResetButton>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{ color: '#666', mt: 1, textAlign: 'center', display: 'block' }}>
                    {t('orDragNewImage', { ns: 'labels', defaultValue: 'o arrastra una nueva imagen aquí' })}
                  </Typography>
                </StyledImageUploadPreviewInnerContainer>
              </StyledImageUploadPreviewContainer>
            ) : (
              <StyledImageUploadButton onClick={openDialog} type="button">
                <StyledImageUploadResetIcon />
                <StyledImageUploadText component="span" variant="body1">
                  {t('clickToUploadImage', { ns: 'labels' })}
                </StyledImageUploadText>
                {t('open', { ns: 'common' })}
              </StyledImageUploadButton>
            )}
          </StyledImageUploadInnerContainer>
        </StyledImageUploadContainer>
        );
      }}
    </Dropzone>
    {crop && (
      <AvatarCropModal
        open={cropOpen}
        imageSrc={cropSrc}
        aspect={cropAspect}
        cropShape={cropShape}
        title="Ajustar imagen"
        saveLabel={t('save', { ns: 'buttons', defaultValue: 'Guardar' })}
        onCancel={closeCrop}
        onSave={handleCropSave}
      />
    )}
    </>
  );
}

ImageUploadAdapter.propTypes = {
  allowedFileTypes: PropTypes.object.isRequired,
  input: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired
  }).isRequired,
  label: PropTypes.string.isRequired,
  meta: PropTypes.shape({
    error: PropTypes.string,
    touched: PropTypes.bool
  }).isRequired,
  crop: PropTypes.bool,
  cropAspect: PropTypes.number,
  cropShape: PropTypes.oneOf(['round', 'rect'])
};

export default ImageUploadAdapter;
